import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { AppThemeName, AppTheme, themes } from './themes';

interface ThemeContextValue {
  themeName: AppThemeName;
  theme: AppTheme;
  setThemeName: (name: AppThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useAppTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useAppTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeName, setThemeName] = useState<AppThemeName>(() => {
    const saved = localStorage.getItem('app-theme');
    if (saved === 'dark' || saved === 'light') {
      return saved as AppThemeName;
    }
    
    // 检测浏览器主题偏好
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light'; // 默认light
  });

  useEffect(() => {
    localStorage.setItem('app-theme', themeName);
    document.documentElement.setAttribute('data-theme', themeName);
    // Also set body color for quick background switch
    const t = themes[themeName];
    document.body.style.background = t.appBackground;
    document.body.style.color = t.textPrimary;
  }, [themeName]);

  const theme = useMemo(() => themes[themeName], [themeName]);

  const toggleTheme = () => setThemeName(themeName === 'light' ? 'dark' : 'light');

  const antdAlgorithm = themeName === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  const antdToken = {
    colorPrimary: theme.accentPrimary,
    colorBgContainer: theme.surfaceBackground,
    colorText: theme.textPrimary,
    colorTextSecondary: theme.textSecondary,
    colorBorder: theme.surfaceBorder,
    borderRadius: 8,
  } as any;

  const value: ThemeContextValue = { themeName, theme, setThemeName, toggleTheme };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={{ algorithm: antdAlgorithm as any, token: antdToken }}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};


